import React, { useMemo, useState, useEffect } from "react";
import { Box, Grid, Typography, Button, alpha } from "@mui/material";
import { useNavigate } from "react-router-dom";

import Filters from "../components/Filters";
import { useMarketFeedInstant } from "../../hooks/useMarketFeedInstant";
import UnifiedMarketCard from "../../components/UnifiedMarketCard";

const WARMED = new Set();
const warmImage = (url) => {
  if (!url) return;
  if (WARMED.has(url)) return;
  WARMED.add(url);
  const img = new Image();
  img.decoding = "async";
  img.src = url;
};

function slugifyTitle(title) {
  const base = String(title || "event")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return base || "event";
}

export default function Stream() {
  const navigate = useNavigate();

  const [searchStreams, setSearchStreams] = useState("");
  const [topStreams, setTopStreams] = useState("All");
  const [chipStreams, setChipStreams] = useState("All");
  const [subStreams, setSubStreams] = useState("All");
  const [visibleStreams, setVisibleStreams] = useState(8);

  const { streams: streamsRaw, loadingStreams } = useMarketFeedInstant({ pageSize: 300 });

  const normalizeStr = (v) => String(v || "").toLowerCase().trim();

  const itemMatches = (item, value) => {
    if (!value || value === "All") return true;
    const w = normalizeStr(value);

    const fields = [
      item.market,
      item.category,
      item.sector,
      item.name,
      item.title,
      item.handle,
      item.creator,
      item.companyName,
      item.marketChip,
      item.marketSub,
      item.type,
      ...(item.tags || []),
      ...(item.assets || []),
    ]
      .filter(Boolean)
      .map(normalizeStr);

    return fields.some((x) => x === w || x.includes(w));
  };

  const applyFilters = (items, { q, top, chip, sub }) => {
    let out = items;
    if (top && top !== "All") out = out.filter((x) => itemMatches(x, top));
    if (chip && chip !== "All") out = out.filter((x) => itemMatches(x, chip));
    if (sub && sub !== "All") out = out.filter((x) => itemMatches(x, sub));
    const s = normalizeStr(q);
    if (s) out = out.filter((x) => itemMatches(x, s));
    return out;
  };

  const goToStreamPage = (item) => {
    if (!item?.id) return;
    const title = item.name || item.title || item.marketTitle || "event";
    const slug = slugifyTitle(title);
    navigate(`/streamevent/${slug}-${item.id}`, { state: { item } });
  };

  const streamsBase = useMemo(() => streamsRaw || [], [streamsRaw]);

  const streamsFiltered = useMemo(() => {
    return applyFilters(streamsBase, {
      q: searchStreams,
      top: topStreams,
      chip: chipStreams,
      sub: subStreams,
    });
  }, [streamsBase, searchStreams, topStreams, chipStreams, subStreams]);

  const streams = useMemo(
    () => (streamsFiltered || []).slice(0, visibleStreams),
    [streamsFiltered, visibleStreams]
  );

  useEffect(() => {
    const first = (streamsFiltered || []).slice(0, 8);
    first.forEach((x) => warmImage(x.posterLow || x.poster || ""));
  }, [streamsFiltered]);

  const streamSkeleton = useMemo(
    () =>
      Array.from({ length: 8 }).map((_, i) => ({
        id: `sk_stream_${i}`,
        type: "stream",
        name: " ",
        handle: " ",
        category: " ",
        flags: {},
        avatarUrl: "",
        poster: "",
        posterLow: "",
        previewSrc: "",
        preview480: "",
        views: 0,
        status: "record",
        description: " ",
      })),
    []
  );

  const showStreamSkeleton = loadingStreams && streams.length === 0;

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Filters
        searchQuery={searchStreams}
        setSearchQuery={setSearchStreams}
        marketTop={topStreams}
        setMarketTop={setTopStreams}
        marketChip={chipStreams}
        setMarketChip={setChipStreams}
        marketSub={subStreams}
        setMarketSub={setSubStreams}
      />

      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mt: 0.5 }}>
        {(showStreamSkeleton ? streamSkeleton : streams).map((item, idx) => (
          <Grid item key={item.id} xs={12} sm={6} md={4} lg={3} sx={{ display: "flex" }}>
            <Box sx={{ width: "100%", display: "flex", minWidth: 0, "& > *": { flex: 1 } }}>
              <UnifiedMarketCard
                item={{ ...item, type: "stream" }}
                priority={idx < 4}
                skeleton={showStreamSkeleton}
                onOpen={() => goToStreamPage(item)}
                onPrimary={() => goToStreamPage(item)}
                onTips={(x) => console.log("tips", x)}
                onBoost={(x) => console.log("boost", x)}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {!showStreamSkeleton && streamsFiltered.length > visibleStreams && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setVisibleStreams((p) => p + 8)}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              color: "#e5e7eb",
              borderColor: "rgba(255,255,255,.16)",
              "&:hover": { borderColor: "rgba(56,189,248,.35)", background: "rgba(56,189,248,.06)" },
            }}
          >
            Show more
          </Button>
        </Box>
      )}

      {!showStreamSkeleton && streamsFiltered.length === 0 && (
        <Box sx={{ textAlign: "center", color: alpha("#e5e7eb", 0.7), py: 6 }}>
          <Typography sx={{ fontWeight: 900, mb: 0.75 }}>No streams yet</Typography>
          <Typography variant="body2">
            Create a document in Firestore: <b>avatar_streams</b>
          </Typography>
        </Box>
      )}
    </Box>
  );
}
